// empty lines below are required to prevent imports from being reordered
import '@fullcalendar/react/dist/vdom';

import FullCalendar, { DatesSetArg, EventContentArg } from '@fullcalendar/react';

import dayGridPlugin from '@fullcalendar/daygrid';
import classNames from 'classnames';
import { DateTime, Interval } from 'luxon';
import { FC, useRef, useState } from 'react';
import Icon from '../../../../components/Icons/Icon';
import { TagTypes } from '../../../../components/Tag/Tag.types';
import { CalendarEvent as CalendarEventType } from '../../../../types/calendar.types';
import { FreezeType } from '../../../../types/freeze.types';
import './Calendar.css';
import styles from './Calendar.module.css';
import { CalendarProps } from './Calendar.types';
import CalendarEvent from './CalendarEvent';
import { useBundles } from '../../../../services/bundles/bundles.service';
import { useTranslation } from 'react-i18next';

const Calendar: FC<CalendarProps> = ({ className, events, setDates, ...props }) => {
  const { t } = useTranslation();
  const { data } = useBundles(true);
  const calendarRef = useRef<FullCalendar>(null);
  const [currentMonth, setCurrentMonth] = useState<string | undefined>('');

  const handleDatesSet = (params: DatesSetArg) => {
    setCurrentMonth(params.view.title);
    setDates({
      startTime: DateTime.fromJSDate(params.start).minus({ months: 2 }).toISODate() ?? '',
      endTime: DateTime.fromJSDate(params.end).plus({ months: 2 }).toISODate() ?? '',
    });
  };

  const handleForwardClick = () => {
    calendarRef.current?.getApi().next();
  };

  const handleBackClick = () => {
    calendarRef.current?.getApi().prev();
  };

  const calendarEvents = events.flatMap((event: CalendarEventType) => {
    switch (event.type) {
      case FreezeType.MAINTENANCE:
        return {
          start: DateTime.fromISO(event.startDate, { zone: 'UTC' }).toISODate() ?? '',
          end: DateTime.fromISO(event.endDate, { zone: 'UTC' }).toISODate() ?? '',
          type: TagTypes.Maintenance,
          title: event.name,
        };
      case FreezeType.GLOBAL_FREEZE:
        return {
          start: DateTime.fromISO(event.startDate, { zone: 'UTC' }).toISODate() ?? '',
          end: DateTime.fromISO(event.endDate, { zone: 'UTC' }).toISODate() ?? '',
          type: TagTypes.Maintenance,
          title: event.name,
        };
      case FreezeType.NATIONAL_PROGRAM:
        return [
          {
            title:
              Array.isArray(data?.data) && data?.data.some((val) => Object.values(val).includes(event?.bundleId))
                ? t('calendar.optIn', { event: event.name })
                : t('calendar.optInComingSoon', { event: event.name }),
            start: DateTime.fromISO(event.optInStartDate, { zone: 'UTC' }).toISODate() ?? '',
            end: DateTime.fromISO(event.startDate, { zone: 'UTC' }).toISODate() ?? '',
            type: TagTypes.NationalDealOptIn,
          },
          {
            title:
              Array.isArray(data?.data) && data?.data.some((val) => Object.values(val).includes(event?.bundleId))
                ? t('calendar.live', { event: event.name })
                : t('calendar.liveComingSoon', { event: event.name }),
            start: DateTime.fromISO(event.startDate, { zone: 'UTC' }).toISODate() ?? '',
            end: DateTime.fromISO(event.endDate, { zone: 'UTC' }).plus({ days: 1 }).toISODate() ?? '',
            type: TagTypes.NationalDealLive,
            detailsLink: Interval.fromDateTimes(
              DateTime.fromISO(event.optInStartDate, { zone: 'UTC' }),
              DateTime.fromISO(event.endDate, { zone: 'UTC' }),
            ).contains(DateTime.now())
              ? `/bundles/${event.bundleId}`
              : undefined,
          },
        ];
      case FreezeType.HOLIDAY:
      default:
        return {
          start: DateTime.fromISO(event.startDate, { zone: 'UTC' }).toISODate() ?? '',
          end: DateTime.fromISO(event.endDate, { zone: 'UTC' }).toISODate() ?? '',
          type: TagTypes.Holiday,
          title: event.name,
        };
    }
  });

  const renderEventContent = (eventCalendarArgs: EventContentArg) => <CalendarEvent {...eventCalendarArgs} />;

  return (
    <div
      className={classNames(styles.card, {
        [styles.hidden]: currentMonth === undefined,
        [className!]: className !== undefined,
      })}
      data-testid="Calendar"
    >
      <div className={styles.headerToolbar}>
        <Icon iconName="ChevronLeft" onClick={handleBackClick} />
        <div className={styles.headerDate}>{currentMonth}</div>
        <Icon iconName="ChevronRight" onClick={handleForwardClick} />
      </div>
      <FullCalendar
        timeZone="UTC"
        height="auto"
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        eventContent={renderEventContent}
        eventBackgroundColor="transparent"
        eventBorderColor="transparent"
        headerToolbar={false}
        dayHeaderFormat={{ weekday: 'long' }}
        datesSet={handleDatesSet}
        ref={calendarRef}
        dayCellContent={({ dayNumberText }) => `00${dayNumberText}`.substring(dayNumberText.length)}
        events={calendarEvents}
        {...props}
      />
    </div>
  );
};

export default Calendar;
